import React from 'react';
import { Button as AriaButton } from 'react-aria-components';
import clsx from 'clsx';
import styles from './CustomButton.module.css';

export interface CustomButtonProps {
  /**
   * Button text
   */
  children?: React.ReactNode;
  /**
   * Button text (alternative to children, useful for Noodl)
   */
  text?: string;
  /**
   * Click handler
   */
  onPress?: () => void;
  /**
   * Button variant
   */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  /**
   * Button size
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Disabled state
   */
  isDisabled?: boolean;
  /**
   * Full width button
   */
  fullWidth?: boolean;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Custom styles
   */
  style?: React.CSSProperties;
  /**
   * Button type
   */
  type?: 'button' | 'submit' | 'reset';
  /**
   * Remove border radius
   */
  hasNoRadius?: boolean;
  /**
   * Remove box shadow
   */
  hasNoBoxShadow?: boolean;
}

/**
 * CustomButton component - an accessible button using React Aria
 */
export const CustomButton: React.FC<CustomButtonProps> = ({
  children,
  text,
  onPress,
  variant = 'primary',
  size = 'medium',
  isDisabled = false,
  fullWidth = false,
  className = '',
  style = {},
  type = 'button',
  hasNoRadius = false,
  hasNoBoxShadow = false,
}) => {
  // Use text prop if children is not provided
  const content = children || text;

  return (
    <AriaButton
      onPress={onPress}
      isDisabled={isDisabled}
      type={type}
      className={clsx(
        styles.button,
        styles[`button${variant.charAt(0).toUpperCase() + variant.slice(1)}`],
        styles[`button${size.charAt(0).toUpperCase() + size.slice(1)}`],
        {
          [styles.buttonDisabled]: isDisabled,
          [styles.buttonFullWidth]: fullWidth,
          [styles.buttonNoRadius]: hasNoRadius,
          [styles.buttonNoBoxShadow]: hasNoBoxShadow,
        },
        className
      )}
      style={style}
    >
      {content}
    </AriaButton>
  );
};

CustomButton.displayName = 'ProdiktCustomButton';
